// What the Configure Benefits plan card shows for a plan's price (BUG-006 /
// DEV-318). The card used to render `calculatePlanPremium(...)?.employeePremium
// || 0`, so a failed, unpriceable or still-loading calculation read as a
// silent "$0.00 per month". Every one of those states now has an explicit
// outcome, and a legitimate $0 employee share reads "Employer paid".
//
// Pure: no React, no fetch. The form feeds it the calculator result plus the
// loader state and renders whatever comes back.
import type { EnrollmentCalculationResult } from "@shared/utils/enrollmentCalculator";
import { ratingAreaGapMessage, type RatingAreaGap } from "@shared/utils/ratingAreaCoverage";
import { isFsaPlanType } from "@shared/schemas/planTypes";
import { isUnpricedPremium } from "./planCardPriceState";

export type PayCadence = {
  /** Pay periods per month — divides a monthly amount into the shown amount. */
  periodsPerMonth: number;
  /** "per bi-weekly paycheck", "per month", "per year". */
  label: string;
  /** Inline form for "$12.34/bi-weekly". */
  short: string;
  /** true when the employee has no (or an unrecognized) pay cycle and monthly was assumed. */
  assumed: boolean;
  /** States the assumption when `assumed`, else null. */
  note: string | null;
};

/**
 * Cadence for a stored employee pay cycle ("Weekly", "Bi-weekly",
 * "Semi-monthly", "Monthly", "Annually"). Blank or unknown values fall back to
 * monthly — and SAY so — instead of quietly labelling the amount "per month".
 */
export function resolvePayCadence(payCycle: string | null | undefined): PayCadence {
  const raw = String(payCycle ?? "").trim();
  const key = raw.toLowerCase().replace(/[-\s_]/g, "");
  switch (key) {
    case "weekly":
      return { periodsPerMonth: 52 / 12, label: "per weekly paycheck", short: "/week", assumed: false, note: null };
    case "biweekly":
      return { periodsPerMonth: 26 / 12, label: "per bi-weekly paycheck", short: "/bi-weekly", assumed: false, note: null };
    case "semimonthly":
      return { periodsPerMonth: 2, label: "per semi-monthly paycheck", short: "/semi-monthly", assumed: false, note: null };
    case "monthly":
      return { periodsPerMonth: 1, label: "per month", short: "/month", assumed: false, note: null };
    case "annually":
    case "annual":
    case "yearly":
      return { periodsPerMonth: 1 / 12, label: "per year", short: "/year", assumed: false, note: null };
    case "":
      return {
        periodsPerMonth: 1,
        label: "per month",
        short: "/month",
        assumed: true,
        note: "No pay cycle on file for this employee — amounts are shown per month. Set the pay cycle on the Employment tab for per-paycheck amounts.",
      };
    default:
      return {
        periodsPerMonth: 1,
        label: "per month",
        short: "/month",
        assumed: true,
        note: `Pay cycle "${raw}" isn't recognized — amounts are shown per month.`,
      };
  }
}

/** Round a per-period amount to cents. */
export function toPerPeriod(monthlyAmount: number, cadence: PayCadence): number {
  return Math.round((monthlyAmount / cadence.periodsPerMonth) * 100) / 100;
}

/**
 * Contribution rows for ONE eligibility-group rule set. The server prices a
 * saved enrollment against the rule set of the employee's snapshotted group;
 * the browser receives every rule set's rows for the plan and must narrow the
 * same way or the first rule set's split wins (client 219's Platinum 90 has a
 * 50% and a 75% set). No rule set, or no rows for it → rows unchanged.
 */
export function scopeContributionsToRuleSet<T extends { contributionRuleSetId?: number | null }>(
  contributions: T[],
  ruleSetId: number | null | undefined,
): T[] {
  if (ruleSetId == null) return contributions;
  const scoped = contributions.filter((c) => Number(c.contributionRuleSetId) === Number(ruleSetId));
  return scoped.length > 0 ? scoped : contributions;
}

export type PlanPriceDisplay =
  | { kind: "loading"; message: string }
  | { kind: "unavailable"; message: string; reasons: string[] }
  | {
      kind: "employer_paid";
      message: string;
      /** Employer's cost at the cadence, for display. */
      employerAmount: number;
      employerMonthly: number;
      cadence: PayCadence;
    }
  | {
      kind: "priced";
      /** Employee cost at the cadence. */
      amount: number;
      employeeMonthly: number;
      employerMonthly: number;
      cadence: PayCadence;
    };

export type DescribePlanPriceInput = {
  /** Calculator output; null when it threw. */
  result: EnrollmentCalculationResult | null | undefined;
  payCycle: string | null | undefined;
  /** Rates/contributions still loading → the card must not show a number yet. */
  ratesLoading?: boolean;
  /** The rate loader failed (message). */
  ratesError?: string | null;
  /** The calculator threw (message). */
  calcError?: string | null;
  /** How many rate rows the loader returned for this plan (0 = none). */
  rateRowCount?: number;
  /** DEV-382: the plan has rates, but none for the group's rating area (message). */
  ratingAreaGap?: string | null;
};

const CANT_PRICE = "Can't price";

/**
 * Decide what the plan card shows. Never maps a failure to $0.00: loading,
 * loader error, calculator throw, calculator warnings and "no rate matched"
 * each come back as an explicit reason. A $0 employee share with a non-zero
 * employer share is a legitimate 100%-employer plan → "Employer paid".
 */
export function describePlanPrice(input: DescribePlanPriceInput): PlanPriceDisplay {
  const cadence = resolvePayCadence(input.payCycle);

  if (input.ratesLoading) {
    return { kind: "loading", message: "Loading rates…" };
  }
  if (input.ratesError) {
    const reason = `rates didn't load (${input.ratesError})`;
    return { kind: "unavailable", message: `${CANT_PRICE}: ${reason}`, reasons: [reason] };
  }
  if (input.ratingAreaGap) {
    const reason = input.ratingAreaGap;
    return { kind: "unavailable", message: `${CANT_PRICE}: ${reason}`, reasons: [reason] };
  }
  const result = input.result;
  if (!result) {
    const reason = input.calcError ? `calculation failed (${input.calcError})` : "calculation failed";
    return { kind: "unavailable", message: `${CANT_PRICE}: ${reason}`, reasons: [reason] };
  }
  const warnings = (result.warnings ?? []).map((w) => String(w)).filter((w) => w.trim().length > 0);
  if (warnings.length > 0) {
    return { kind: "unavailable", message: `${CANT_PRICE}: ${warnings.join("; ")}`, reasons: warnings };
  }

  const employeeMonthly = Number(result.employeePremium) || 0;
  const employerMonthly = Number(result.employerPremium) || 0;
  const totalMonthly = Number(result.totalMonthlyRate) || 0;

  if (totalMonthly <= 0 && employeeMonthly <= 0 && employerMonthly <= 0) {
    const reason =
      input.rateRowCount === 0
        ? "no rates loaded for this plan — check the plan's rate table for this client"
        : "no rate matched this employee on the plan's rate table";
    return { kind: "unavailable", message: `${CANT_PRICE}: ${reason}`, reasons: [reason] };
  }

  if (employeeMonthly <= 0 && employerMonthly > 0) {
    return {
      kind: "employer_paid",
      message: "Employer paid",
      employerAmount: toPerPeriod(employerMonthly, cadence),
      employerMonthly,
      cadence,
    };
  }

  return {
    kind: "priced",
    amount: toPerPeriod(employeeMonthly, cadence),
    employeeMonthly,
    employerMonthly,
    cadence,
  };
}

/**
 * DEV-382: "Can't price: …" for a plan with rates, but none for the group's
 * rating area, else null. `gap` is the server's enrollment.ratingAreaGap or the
 * bulk rate loader's ratingAreaGapsByPlanId entry.
 */
export function ratingAreaGapPriceMessage(
  gap: RatingAreaGap | null | undefined,
  planName?: string | null,
): string | null {
  if (!gap || !Number.isFinite(Number(gap.ratingAreaId))) return null;
  return `${CANT_PRICE}: ${ratingAreaGapMessage(gap, planName)}`;
}

/**
 * DEV-382: an enrollment's monthly employee premium for a total, or null when
 * it can't be priced (the server flagged a rating-area gap and returned 0).
 */
export function enrollmentEmployeeMonthly(enrollment: any): number | null {
  if (enrollment?.ratingAreaGap) return null;
  const v = Number(enrollment?.employeePremium ?? enrollment?.premium?.employeePremium ?? 0);
  const employeeMonthly = Number.isFinite(v) ? v : 0;
  // DEV-389: an all-zero price is unpriced. A missing total is unknown, not
  // unpriced; FSA $0 is a real "no election".
  const total = enrollment?.totalPremium ?? enrollment?.premium?.totalMonthlyRate;
  const planType = enrollment?.plan?.type ?? enrollment?.planType;
  if (total != null && !isFsaPlanType(planType) && isUnpricedPremium({ totalMonthlyRate: total, employeePremium: employeeMonthly })) {
    return null;
  }
  return employeeMonthly;
}

/**
 * DEV-382: sum priced amounts; a null amount is never summed as $0 — its label
 * lands in `unpricedPlans` so the caller withholds the total and names it
 * (same rule as the Configure Benefits total).
 */
export function totalPricedMonthly(
  items: Array<{ label: string; monthlyAmount: number | null }>,
): { totalMonthly: number; unpricedPlans: string[] } {
  let totalMonthly = 0;
  const unpricedPlans: string[] = [];
  for (const it of items) {
    if (it.monthlyAmount == null) unpricedPlans.push(it.label);
    else totalMonthly += it.monthlyAmount;
  }
  return { totalMonthly, unpricedPlans };
}
